"use client";

/**
 * /v3/a — "Memorandum, evolved." The /v2 visual system: serif headlines,
 * hairline section rules, mono labels, narrator interstitials. Variation A is
 * the chosen base (Round 5); Variation B stays on disk as reference only.
 *
 * Round-5 change: Variation B's sticky index rail is transplanted here — a
 * slim left-hand index ("The product / The proof / The value / Start") that
 * FLOATS for the entire scroll, current section highlighted gold via an
 * IntersectionObserver scrollspy plus a bottom-of-page sentinel for the last
 * section. It collapses to a horizontal bar under 900px.
 *
 * The stickiness bug (Jay: the rail disappeared after the first sections in
 * B) was `overflow-x: hidden` on the page root: `hidden` establishes a scroll
 * container, and because the root doesn't actually scroll internally (the
 * viewport does), `position: sticky` inside it had nothing to stick to and
 * scrolled away. Fixed by using `overflow-x: clip` on `.v3aroot` — clip
 * prevents horizontal overflow WITHOUT creating a scroll container, so the
 * rail sticks against the viewport for the whole page.
 *
 * All copy comes from ../content.ts — this file only owns structure/CSS.
 * Reused blocks (<ShowcaseRowV3 />, <GlassEngineV3 />, <AdvisorDesk />, the
 * phone band) come from ../shared.tsx; app/v2/ is never modified.
 */

import { Fragment, useEffect, useRef, useState } from "react";
import {
  ACTS,
  ActId,
  CAPACITY,
  CEILING,
  CLOSING_ONELINER,
  COMPLIANCE,
  FAQ,
  FOOTER_TEXT,
  HERO,
  NARRATOR_1,
  NARRATOR_2,
  NARRATOR_3,
  PILOT_CLOSE,
  PROMISE,
  THREE_CARDS,
  TOPBAR,
  WHY_NOW,
} from "../content";
import {
  CALENDLY_URL,
  REUSE_CSS,
  ReusedProductBlock,
  ReusedTrustBlock,
  renderInline,
} from "../shared";
import { CALC_CSS, Calculator } from "../Calculator";

function Narrator({ text }: { text: string }) {
  return <p className="narrator">{text}</p>;
}

function ActMarker({ id }: { id: ActId }) {
  return <div className="actmarker" id={`act-${id}`} data-act-marker={id} />;
}

/**
 * Scrollspy for the sticky rail.
 *
 * REWRITTEN (Round 7) — the rail was stuck. The old version used an
 * IntersectionObserver over a narrow band ("-15% 0px -70% 0px") and set the
 * active act only on `isIntersecting`. Two bugs came out of that:
 *
 *   1. It never handled a marker LEAVING, so the highlight only ever moved
 *      forward. Scrolling back up left it on whatever act you had reached.
 *   2. Marker I sits at document y = -12 (the .actmarker rule offsets each
 *      marker by -90px so anchor links clear the header). That is ABOVE the
 *      trigger band at every scroll position, so act I could never fire at
 *      all — meaning once you scrolled past act II, "The product" could never
 *      be selected again. Measured on the live page: at the very top of the
 *      document the rail read "The proof".
 *
 * The fix is to stop treating this as an event problem. On scroll we simply
 * ask which marker was the last one to pass a trigger line a quarter of the
 * way down the viewport. That is correct in both directions by construction,
 * needs no sentinel for the final act (the bottom-of-page clamp covers it),
 * and cannot get stuck.
 *
 * Reads are rAF-throttled so a fast scroll costs one layout read per frame.
 */
const SPY_LINE_RATIO = 0.25; // trigger line, as a fraction of viewport height
const SPY_BOTTOM_SLACK = 4; // px of rounding slop when detecting page bottom

function useActiveAct() {
  const rootRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<ActId>("I");

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const markers = Array.from(
      root.querySelectorAll<HTMLElement>("[data-act-marker]")
    );
    if (markers.length === 0) return;

    const lastId = markers[markers.length - 1].getAttribute(
      "data-act-marker"
    ) as ActId;

    let frame = 0;
    const measure = () => {
      frame = 0;
      const line = window.innerHeight * SPY_LINE_RATIO;
      let current = markers[0].getAttribute("data-act-marker") as ActId;
      for (const marker of markers) {
        if (marker.getBoundingClientRect().top > line) break;
        current = marker.getAttribute("data-act-marker") as ActId;
      }
      // At max scroll the final act has too little trailing content to push
      // its marker above the line, so clamp to it whenever the page bottom
      // is reached. This replaces the old bottom sentinel.
      const atBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - SPY_BOTTOM_SLACK;
      setActive(atBottom ? lastId : current);
    };

    const onScroll = () => {
      if (frame === 0) frame = requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return { rootRef, active };
}

export function V3AClient() {
  const { rootRef, active } = useActiveAct();

  return (
    <div className="v3aroot">
      <style>{CSS}</style>
      <style>{REUSE_CSS}</style>
      <style>{CALC_CSS}</style>

      <div className="v3a" ref={rootRef}>
        {/* Top bar (Round 7). It used to hold the wordmark and ~1,010px of
            nothing. It now carries the category line and the demo button,
            which between them cost the hero 117px it could not spare — see
            the TOPBAR note in ../content.ts. The section index deliberately
            did NOT move up here (Jay's call): it stays on the left rail. */}
        <div className="top">
          <div className="topbar">
            <div className="wm">
              Kreayt<span className="dot">.</span>
            </div>
            <div className="barline">{TOPBAR.categoryLine}</div>
            <div className="barright">
              <a className="barlink" href={TOPBAR.notes.href}>
                {TOPBAR.notes.label}
              </a>
              <a
                className="barcta"
                href={TOPBAR.cta.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {TOPBAR.cta.label}
              </a>
            </div>
          </div>
        </div>

        <div className="shell">
          <aside className="railbar">
            <nav className="railnav" aria-label="Sections">
              {ACTS.map((act) => (
                <a
                  key={act.id}
                  href={`#act-${act.id}`}
                  className={active === act.id ? "on" : undefined}
                >
                  <span className="railword">{act.railLabel}</span>
                </a>
              ))}
            </nav>
          </aside>

          <div className="col">
            {/* ACT I — hero. One headline, then the product. The explainer,
                the white-label line and the guardrail moved to the .promise
                section below the showcase; the category line and the demo
                button moved into the top bar. That is what lets the
                four-door row clear the fold on a laptop. */}
            <ActMarker id="I" />
            <section className="hero">
              <h1 className="hl">{renderInline(HERO.h1)}</h1>
            </section>

            <Narrator text={NARRATOR_1} />

            <div className="v3reuse">
              <ReusedProductBlock />
            </div>

            {/* The promise, read AFTER the demo rather than before it. */}
            <section className="promise">
              <p className="core">{PROMISE.core}</p>
              <p className="whitelabel">{PROMISE.whiteLabel}</p>
              <div className="guardrail">
                <p>{renderInline(PROMISE.guardrail)}</p>
              </div>
            </section>

            {/* ACT II — trust */}
            <ActMarker id="II" />
            <Narrator text={NARRATOR_2} />

            <div className="v3reuse">
              <ReusedTrustBlock />
            </div>

            <section className="band comp-section" id="compliance">
              <div className="comp">
                <div className="lead">{COMPLIANCE.lead}</div>
                <ul className="clist">
                  {COMPLIANCE.checklist.map((item) => (
                    <li key={item.text} className={item.wip ? "wip" : undefined}>
                      {item.text}
                    </li>
                  ))}
                </ul>
                <div className="liability">
                  <div className="liability-q">
                    {COMPLIANCE.liability.question}
                  </div>
                  <p className="liability-a">{COMPLIANCE.liability.answer}</p>
                </div>
                <p className="compnote">
                  {COMPLIANCE.note} {COMPLIANCE.briefLine}
                </p>
                <div className="chain">
                  {COMPLIANCE.chain.map((node, i) => (
                    <Fragment key={node.label}>
                      {i > 0 && <div className="link" />}
                      <div className="node">
                        <span className={`d${node.ok ? " ok" : ""}`} />
                        <span>{node.label}</span>
                      </div>
                    </Fragment>
                  ))}
                </div>
              </div>
            </section>

            {/* Structural freedom: three cards placed between Act II and III */}
            <section className="band">
              <div className="lbl">{THREE_CARDS.label}</div>
              <div className="caps">
                {THREE_CARDS.cards.map((card) => (
                  <div className="c" key={card.title}>
                    <h4>{card.title}</h4>
                    <p>{renderInline(card.body)}</p>
                    {card.premium && (
                      <span className="prem">{renderInline(card.premium)}</span>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* ACT III — worth */}
            <ActMarker id="III" />
            <Narrator text={NARRATOR_3} />

            <section className="band">
              <p className="pain">
                {CAPACITY.body} <span className="src">{CAPACITY.citation}</span>
              </p>
            </section>

            <section className="band">
              <Calculator />
            </section>

            {/* Scope, not price. The price section that used to wrap this
                (two rate variants, the ?pricing switch and the "what covered
                includes" box) is gone from the homepage entirely — it lives
                on /pilot now. The ceiling stayed because it never named a
                number: it answers "what do I actually get, and what stops it
                running away", which is a product question. */}
            <section className="band ceiling-section">
              <h3>{CEILING.h3}</h3>
              <ul className="ceilinglist">
                {CEILING.checklist.map((item) => (
                  <li key={item.text} className={item.status}>
                    {renderInline(item.text)}
                  </li>
                ))}
              </ul>
              <p className="ceiling-key">{renderInline(CEILING.keyLine)}</p>
            </section>

            <section className="band">
              <div className="lbl">{WHY_NOW.label}</div>
              <p className="pain">{WHY_NOW.body}</p>
              <p className="capline">{WHY_NOW.capacityLine}</p>
            </section>

            {/* ACT IV — CLOSE */}
            <ActMarker id="IV" />
            <section className="band">
              <p className="oneliner">{renderInline(CLOSING_ONELINER)}</p>
            </section>

            <section className="band faq">
              <div className="lbl">{FAQ.label}</div>
              {FAQ.items.map((item) => (
                <details key={item.q}>
                  <summary>{item.q}</summary>
                  <p>{item.a}</p>
                </details>
              ))}
            </section>
          </div>
        </div>

        <div className="close">
          <div className="h">
            {PILOT_CLOSE.heading}
            <span>{PILOT_CLOSE.sub}</span>
          </div>
          <div className="closeactions">
            <a
              className="btn p"
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ background: "#F4F1E9", color: "#16324F" }}
            >
              {PILOT_CLOSE.cta.label}
            </a>
            {/* The only route from the homepage to the commercial detail.
                /pilot is unlisted — not in the rail, not in the footer. */}
            <a className="closelink" href={PILOT_CLOSE.detailLink.href}>
              {PILOT_CLOSE.detailLink.label}
            </a>
          </div>
        </div>
        <div className="foot" data-bottom-sentinel="">
          {FOOTER_TEXT}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styling — evolved from /v2's Memorandum system, scoped under .v3a, now with
// a sticky left rail (Round 5). Reused blocks (showcase/engine/desk/phoneband)
// get their styling from REUSE_CSS (see ../shared.tsx).
// ---------------------------------------------------------------------------
const CSS = `
  .v3aroot, .v3aroot * { margin: 0; padding: 0; box-sizing: border-box; }
  .v3aroot {
    --serif-memo: "Charter", "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif;
    --sans-sys: -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif;
    --mono: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
    font-family: var(--sans-sys);
    -webkit-font-smoothing: antialiased;
    background: #FBFAF7;
    min-height: 100vh;
    /* clip (not hidden) prevents horizontal overflow WITHOUT creating a
       scroll container — so the sticky rail below sticks to the viewport for
       the whole page instead of scrolling away after the first sections. */
    overflow-x: clip;
  }

  .v3aroot .v3a { background:#FBFAF7; color:#14202C; font-family:var(--sans-sys); }

  /* Top bar (Round 7) — wordmark, category line, demo button. Previously the
     wordmark alone in a 1,100px band, i.e. ~1,010px of dead width. The button
     is what makes the bar 66px instead of 54px; that 12px is the only height
     this rebalance spends, against 117px it takes out of the hero. */
  .v3aroot .v3a .top { border-bottom:1px solid #E6E1D6; }
  .v3aroot .v3a .topbar { display:flex; align-items:center; justify-content:space-between; gap:30px; max-width:1280px; margin:0 auto; padding:10px 40px; }
  .v3aroot .v3a .wm { font-family:var(--serif-memo); font-weight:700; font-size:22px; color:#16324F; letter-spacing:-.2px; flex-shrink:0; }
  .v3aroot .v3a .wm .dot{ color:#9C7A3C; }
  .v3aroot .v3a .barline { font-size:11.5px; letter-spacing:1.6px; text-transform:uppercase; color:#55607a; font-weight:700; text-align:center; }
  .v3aroot .v3a .barcta { flex-shrink:0; background:#16324F; color:#F4F1E9; font-size:13.5px; font-weight:600; text-decoration:none; padding:12px 22px; border-radius:2px; }
  .v3aroot .v3a .barcta:hover { background:#1E4066; }
  .v3aroot .v3a .barcta:focus-visible { outline:2px solid #9C7A3C; outline-offset:2px; }
  /* "Notes" nav link beside the demo button — same voice as .barline. */
  .v3aroot .v3a .barright { display:flex; align-items:center; gap:22px; flex-shrink:0; }
  .v3aroot .v3a .barlink { font-size:11.5px; letter-spacing:1.6px; text-transform:uppercase; color:#16324F; font-weight:700; text-decoration:none; border-bottom:1px solid #C9BEA6; padding-bottom:2px; }
  .v3aroot .v3a .barlink:hover { border-bottom-color:#9C7A3C; }
  /* Under 1100px the category line is the first thing to go: the wordmark
     says who, the button says what to do, and the line repeats on the page. */
  @media(max-width:1100px){ .v3aroot .v3a .barline { display:none; } }
  @media(max-width:640px){
    .v3aroot .v3a .topbar { padding:10px 22px; gap:14px; }
    .v3aroot .v3a .barcta { font-size:12.5px; padding:10px 16px; }
    .v3aroot .v3a .barright { gap:14px; }
  }

  /* Shell: sticky rail + reading column (Round 5 transplant from Variation B) */
  .v3aroot .v3a .shell { display:flex; align-items:flex-start; max-width:1280px; margin:0 auto; gap:36px; padding:0 40px; }
  .v3aroot .v3a .col { flex:1; min-width:0; max-width:1020px; }

  .v3aroot .v3a .railbar { width:172px; flex-shrink:0; position:sticky; top:0; align-self:flex-start; padding-top:56px; }
  .v3aroot .v3a .railnav { display:flex; flex-direction:column; gap:16px; }
  .v3aroot .v3a .railnav a { display:flex; align-items:baseline; text-decoration:none; color:#9AA1AB; border-left:2px solid #E6E1D6; padding-left:12px; transition:color .2s, border-color .2s; }
  .v3aroot .v3a .railnav a:hover { color:#55607a; }
  .v3aroot .v3a .railnav a.on { color:#16324F; border-color:#9C7A3C; }
  .v3aroot .v3a .railword { font-size:13px; font-weight:600; line-height:1.3; }
  .v3aroot .v3a .actmarker { position:relative; top:-90px; height:0; }

  @media(max-width:900px){
    .v3aroot .v3a .shell { flex-direction:column; gap:0; padding:0; max-width:100%; }
    .v3aroot .v3a .railbar { width:100%; position:sticky; top:0; z-index:20; padding-top:0; background:#FBFAF7; border-bottom:1px solid #E6E1D6; }
    .v3aroot .v3a .railnav { flex-direction:row; gap:0; overflow-x:auto; }
    .v3aroot .v3a .railnav a { border-left:none; border-bottom:2px solid #E6E1D6; padding:10px 16px; flex-shrink:0; }
    .v3aroot .v3a .col { max-width:100%; padding:0 22px; }
  }
  @media(max-width:640px){ .v3aroot .v3a .col{ padding:0 22px; } }

  /* Hero (Round 7) — the headline and nothing else. Everything that used to
     stand between it and the product either moved into the top bar or below
     the showcase. Measured at 1440×682: the product row now starts at 270px
     and lands whole above the fold with the video at its existing size. */
  .v3aroot .v3a .hero { padding:36px 0 20px; text-align:center; }
  .v3aroot .v3a .hl { font-family:var(--serif-memo); font-weight:600; font-size:clamp(30px,4.4vw,42px); line-height:1.16; letter-spacing:-.5px; max-width:880px; margin:0 auto; text-wrap:balance; color:#14202C; }
  .v3aroot .v3a .btn { font-size:14.5px; font-weight:600; text-decoration:none; padding:13px 26px; border-radius:2px; display:inline-block; }
  .v3aroot .v3a .btn.p { background:#16324F; color:#F4F1E9; }
  .v3aroot .v3a .btn.s { color:#16324F; border:1px solid #C9BEA6; }

  /* The caption for the product row. It sits with the row, so its old 52px
     margins are now 14 — the gap it used to open is the gap the row's own
     44px top margin also opened, and the two together cost 127px. */
  .v3aroot .v3a .narrator { font-family:var(--serif-memo); font-style:italic; color:#9C7A3C; font-size:15px; text-align:center; max-width:620px; margin:14px auto; line-height:1.5; }
  .v3aroot .v3a .v3reuse .showcase { margin-top:0; }

  /* The promise, below the product. Same three blocks that used to sit above
     it, same wording, read after the demo instead of before it. */
  .v3aroot .v3a .promise { padding:40px 0 10px; text-align:center; }
  .v3aroot .v3a .core { font-family:var(--serif-memo); font-size:19px; line-height:1.55; color:#3a4658; max-width:740px; margin:0 auto; }
  .v3aroot .v3a .whitelabel { font-size:14px; font-weight:700; color:#16324F; margin:16px auto 0; max-width:600px; }
  .v3aroot .v3a .guardrail { max-width:640px; margin:28px auto 0; padding-top:20px; border-top:1px solid #E6E1D6; text-align:center; }
  .v3aroot .v3a .guardrail p { font-family:var(--serif-memo); font-size:15.5px; line-height:1.55; color:#3a4658; }
  .v3aroot .v3a .guardrail p strong { color:#1E6B52; }

  /* Short windows. The product row is sized in vh so it shrinks with the
     window; the hero above it is fixed px and does not, so below roughly
     660px tall the hero starts eating the row again. This is the one rule
     that keeps the whole-product promise on small laptops. */
  @media (max-height: 660px) {
    .v3aroot .v3a .topbar { padding:8px 40px; }
    .v3aroot .v3a .hero { padding:22px 0 12px; }
    .v3aroot .v3a .hl { font-size:clamp(26px,3.6vw,34px); }
    .v3aroot .v3a .narrator { margin:10px auto; font-size:14px; }
  }

  .v3aroot .v3a section.band { padding:54px 0; border-top:1px solid #E6E1D6; }
  .v3aroot .v3a .lbl { font-size:11px; letter-spacing:1.4px; text-transform:uppercase; color:#16324F; font-weight:700; margin-bottom:26px; }
  .v3aroot .v3a .pain { font-family:var(--serif-memo); font-size:19px; line-height:1.6; color:#3a4658; max-width:680px; }
  .v3aroot .v3a .pain .src { font-size:12.5px; color:#9AA1AB; font-family:var(--sans-sys); font-style:normal; }
  .v3aroot .v3a .capline { font-family:var(--serif-memo); font-style:italic; color:#9C7A3C; font-size:15px; margin-top:18px; }

  .v3aroot .v3a .caps { display:grid; grid-template-columns:repeat(3,1fr); gap:0; }
  @media(max-width:700px){ .v3aroot .v3a .caps{grid-template-columns:1fr; gap:26px;} }
  .v3aroot .v3a .caps .c { padding:0 24px; border-left:1px solid #E6E1D6; }
  .v3aroot .v3a .caps .c:first-child{ border-left:none; padding-left:0; }
  @media(max-width:700px){ .v3aroot .v3a .caps .c{border-left:none;padding:0;} }
  .v3aroot .v3a .caps h4 { font-family:var(--serif-memo); font-size:19px; color:#16324F; margin-bottom:8px; }
  .v3aroot .v3a .caps p { font-size:13.5px; color:#4a5568; line-height:1.55; }
  .v3aroot .v3a .caps p strong { color:#14202C; font-weight:600; }
  .v3aroot .v3a .caps .prem { display:block; margin-top:10px; padding-top:9px; border-top:1px solid #E6E1D6; font-size:12px; color:#4a5568; }
  .v3aroot .v3a .caps .prem strong { color:#9C7A3C; }

  .v3aroot .v3a .comp-section { padding-top:0; border-top:none; }
  .v3aroot .v3a .comp { background:#F0F6F2; border-left:3px solid #1E6B52; padding:26px 30px; max-width:760px; }
  .v3aroot .v3a .comp .lead { font-family:var(--serif-memo); font-weight:700; color:#1E6B52; font-size:17px; margin-bottom:14px; }
  .v3aroot .v3a .clist { list-style:none; margin-bottom:4px; }
  .v3aroot .v3a .clist li { font-size:13px; color:#14202C; line-height:1.5; padding-left:20px; position:relative; margin-bottom:6px; }
  .v3aroot .v3a .clist li::before { content:"✓"; position:absolute; left:0; color:#1E6B52; font-weight:700; }
  .v3aroot .v3a .clist li.wip { color:#55607a; }
  .v3aroot .v3a .clist li.wip::before { content:"◐"; color:#B98A2E; }
  .v3aroot .v3a .liability { margin-top:16px; padding-top:14px; border-top:1px dashed #C7DBCC; }
  .v3aroot .v3a .liability-q { font-family:var(--serif-memo); font-weight:700; font-size:13.5px; color:#16324F; margin-bottom:6px; }
  .v3aroot .v3a .liability-a { font-size:12.5px; color:#3a4658; line-height:1.55; }
  .v3aroot .v3a .compnote { font-size:12px; color:#55607a; margin-top:12px; }
  .v3aroot .v3a .chain { display:flex; align-items:center; flex-wrap:wrap; gap:6px; margin-top:16px; }
  .v3aroot .v3a .node { display:flex; align-items:center; gap:7px; }
  .v3aroot .v3a .node .d { width:8px;height:8px;border-radius:50%;background:#16324F; }
  .v3aroot .v3a .node .d.ok{ background:#1E6B52; }
  .v3aroot .v3a .node span{ font-size:12px; color:#55607a; }
  .v3aroot .v3a .link { flex:1; min-width:16px; height:1px; background:#CBD5C5; margin:0 8px; }

  .v3aroot .v3a .oneliner { font-family:var(--serif-memo); font-size:18px; line-height:1.6; color:#3a4658; max-width:680px; margin:0 auto; text-align:center; }
  .v3aroot .v3a .oneliner strong { color:#16324F; font-weight:700; }

  /* Price CSS (.price-h2, .price-body, .tiers, .tier*, .included) was deleted
     in Round 7 along with the section it styled. Pricing lives on /pilot. */

  /* Scope section — the ceiling, promoted out of the old price section into
     a band of its own. It answers what you get and what stops it running
     away, and it never named a number. */
  .v3aroot .v3a .ceiling-section { max-width:700px; }
  .v3aroot .v3a .ceiling-section h3 { font-family:var(--serif-memo); font-size:20px; font-weight:600; color:#14202C; margin-bottom:16px; }
  .v3aroot .v3a .ceilinglist { list-style:none; margin-bottom:18px; }
  .v3aroot .v3a .ceilinglist li { font-size:13.5px; color:#3a4658; line-height:1.55; padding-left:24px; position:relative; margin-bottom:10px; }
  .v3aroot .v3a .ceilinglist li strong { color:#14202C; }
  .v3aroot .v3a .ceilinglist li.check::before { content:"✓"; position:absolute; left:0; color:#1E6B52; font-weight:700; }
  .v3aroot .v3a .ceilinglist li.half::before { content:"◐"; position:absolute; left:0; color:#B98A2E; font-weight:700; }
  .v3aroot .v3a .ceiling-key { font-family:var(--serif-memo); font-size:15px; line-height:1.55; color:#3a4658; }
  .v3aroot .v3a .ceiling-key strong { color:#16324F; }

  .v3aroot .v3a .faq details { border-bottom:1px solid #E6E1D6; padding:14px 0; max-width:720px; }
  .v3aroot .v3a .faq summary { font-family:var(--serif-memo); font-size:16px; color:#16324F; font-weight:600; cursor:pointer; }
  .v3aroot .v3a .faq p { font-size:13.5px; color:#4a5568; line-height:1.6; margin-top:10px; max-width:64ch; }

  .v3aroot .v3a .close { background:#16324F; color:#F4F1E9; padding:32px 40px; display:flex; justify-content:space-between; align-items:center; gap:20px; flex-wrap:wrap; max-width:1060px; margin:0 auto; }
  .v3aroot .v3a .close .h { font-family:var(--serif-memo); font-size:22px; font-weight:600; }
  .v3aroot .v3a .close .h span{ display:block; font-family:var(--sans-sys); font-size:13px; opacity:.85; font-weight:400; margin-top:6px; max-width:460px; line-height:1.5; }
  .v3aroot .v3a .closeactions { display:flex; flex-direction:column; align-items:flex-end; gap:10px; }
  .v3aroot .v3a .closelink { font-size:12.5px; color:#C9A85C; text-decoration:none; border-bottom:1px solid #C9A85C55; padding-bottom:1px; }
  .v3aroot .v3a .closelink:hover { color:#F4F1E9; border-bottom-color:#F4F1E9; }
  .v3aroot .v3a .closelink:focus-visible { outline:2px solid #C9A85C; outline-offset:3px; }
  @media(max-width:640px){ .v3aroot .v3a .closeactions { align-items:flex-start; } }
  .v3aroot .v3a .foot { text-align:center; color:#9AA1AB; font-size:12px; padding:28px 0 40px; }

  @media (prefers-reduced-motion: reduce){ .v3aroot *{ animation:none !important; } }
`;
